import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import './Purchases.css';

const apiBaseURL = (process.env.REACT_APP_API_BASE_URL || 'https://api.vehiclesolution.co').replace(/\/+$/, '');

function Purchases({ onLogout }) {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return date.toISOString().slice(0, 10);
  });

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseURL}/api/purchase?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(searchTerm.trim())}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load purchases');
      }

      setPurchases(data.purchases || []);
      setPagination(data.pagination || { page, pageSize, total: 0, totalPages: 1 });
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load purchases');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, fromDate, toDate]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const formatDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString();
  };

  const formatMoney = (value) => Number(value || 0).toFixed(2);

  return (
    <div className="home-container app-shell-fixed">
      <Sidebar onLogout={onLogout} />
      <div className="main-section app-fixed-main">
        <div className="purchases-container app-scroll-layout">
          <header className="purchases-header">
            <button className="app-back-button" onClick={() => navigate('/inventory')}>
              Back
            </button>
            <div>
              <h1>Purchase Items</h1>
              <p>View all purchase entries, search records, or filter by date range.</p>
            </div>
            <button className="btn-refresh" onClick={fetchPurchases} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </header>

          <div className="purchases-filters">
            <input
              type="text"
              className="purchases-search"
              placeholder="Search by GRN, part no, barcode, part id, user id..."
              value={searchTerm}
              onChange={(e) => {
                setPage(1);
                setSearchTerm(e.target.value);
              }}
            />
            <div className="date-filter">
              <label htmlFor="fromDate">From Date</label>
              <input
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setPage(1);
                  setFromDate(e.target.value);
                }}
              />
            </div>
            <div className="date-filter">
              <label htmlFor="toDate">To Date</label>
              <input
                id="toDate"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setPage(1);
                  setToDate(e.target.value);
                }}
              />
            </div>
            <div className="date-filter">
              <label htmlFor="pageSize">Rows</label>
              <select
                id="pageSize"
                className="purchases-page-size"
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(parseInt(e.target.value, 10));
                }}
              >
                <option value={10}>10 rows</option>
                <option value={20}>20 rows</option>
                <option value={50}>50 rows</option>
              </select>
            </div>
          </div>

          {error && <div className="purchases-error">{error}</div>}

          <div className="app-scroll-results">
            <div className="purchases-table-wrapper app-grid-scroll">
              {loading ? (
                <div className="purchases-empty">Loading purchase items...</div>
              ) : purchases.length === 0 ? (
                <div className="purchases-empty">No purchase items found for the current filters.</div>
              ) : (
                <table className="purchases-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>GRN No</th>
                      <th>Purchase Date</th>
                      <th>Part ID</th>
                      <th>Part No</th>
                      <th>Part_type</th>
                      <th>Unit Price</th>
                      <th>Qty</th>
                      <th>Total</th>
                      <th>Barcode</th>
                      <th>Sale Price</th>
                      <th>User ID</th>
                      <th>Invoice Date</th>
                      <th>Is Send</th>
                      <th>Barprint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((purchase) => (
                      <tr key={purchase.purchase_id}>
                        <td>{purchase.purchase_id}</td>
                        <td>{purchase.grn_no || '-'}</td>
                        <td>{formatDate(purchase.purchase_date)}</td>
                        <td>{purchase.part_id ?? '-'}</td>
                        <td>{purchase.part_no || '-'}</td>
                        <td>{purchase.Part_type || '-'}</td>
                        <td>{formatMoney(purchase.unit_price)}</td>
                        <td>{purchase.qty ?? 0}</td>
                        <td>{formatMoney(purchase.total)}</td>
                        <td>{purchase.tx_no || '-'}</td>
                        <td>{formatMoney(purchase.Sale_Price)}</td>
                        <td>{purchase.user_id ?? '-'}</td>
                        <td>{formatDate(purchase.Invoice_Date)}</td>
                        <td>{purchase.IsSend ? 'Yes' : 'No'}</td>
                        <td>
                          <button
                            className="btn-barprint"
                            type="button"
                            onClick={() => navigate(`/inventory/purchases/${purchase.purchase_id}/barprint`)}
                          >
                            Barprint
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {!loading && !error && (
              <div className="purchases-pagination">
                <div className="purchases-page-info">
                  Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
                </div>
                <div className="purchases-page-buttons">
                  <button
                    className="btn-refresh"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-refresh"
                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Purchases;
